import type { ProjectVertical } from "./projectVertical";

import * as z from "zod";

/**
 * The trade a project is in (`project.category`). Whatever a person types or their website says is filed under one of
 * these, and its `vertical` is the workflow set the project is offered unless a Shopify store says otherwise.
 * Stored as text, so a category is added here without a migration. Listed in the order people are shown.
 */
export const projectCategorySchema = z
  .enum([
    "food-beverage",
    "beauty-skincare",
    "apparel-accessories",
    "health-supplements",
    "home-lifestyle",
    "pets",
    "electronics",
    "merch-creator",
    "b2b-software",
    "consumer-apps",
    "games",
    "developer-tools",
    "restaurants",
    "shops",
    "salon-spa",
    "fitness-wellness",
    "healthcare",
    "home-services",
    "real-estate",
    "professional-services",
  ])
  .describe("ProjectCategory");

export type ProjectCategory = z.infer<typeof projectCategorySchema>;

/** Who last set a project's category (`project.category_source`); detection never writes over `user`. */
export const projectCategorySourceSchema = z
  .enum(["unset", "detected", "user"])
  .describe("ProjectCategorySource");

export type ProjectCategorySource = z.infer<typeof projectCategorySourceSchema>;

type ProjectCategoryDefinition = {
  /** The workflow set a project in this category is offered. */
  vertical: ProjectVertical;
  /** What a person picks it by. */
  label: string;
  /** How it reads before "business" mid-sentence; null where no trade is named. */
  businessName: string | null;
  /** What the business is called in a sentence: "OLIPOP is a food and drink brand." */
  noun: string;
  /** What the classifier is told belongs here. */
  hint: string;
};

export const PROJECT_CATEGORIES: Record<
  ProjectCategory,
  ProjectCategoryDefinition
> = {
  "food-beverage": {
    vertical: "ecommerce",
    label: "Food and drink",
    businessName: "food and drink",
    noun: "food and drink brand",
    hint: "Sells food or drink products",
  },
  "beauty-skincare": {
    vertical: "ecommerce",
    label: "Beauty and skincare",
    businessName: "beauty and skincare",
    noun: "beauty brand",
    hint: "Sells beauty, skincare, grooming or hair products",
  },
  "apparel-accessories": {
    vertical: "ecommerce",
    label: "Apparel and accessories",
    businessName: "apparel and accessories",
    noun: "apparel brand",
    hint: "Sells clothing, shoes, eyewear, bags or jewellery",
  },
  "health-supplements": {
    vertical: "ecommerce",
    label: "Health and supplements",
    businessName: "health and supplements",
    noun: "supplements brand",
    hint: "Sells vitamins, supplements or wellness products",
  },
  "home-lifestyle": {
    vertical: "ecommerce",
    label: "Home and lifestyle",
    businessName: "home and lifestyle",
    noun: "home goods brand",
    hint: "Sells furniture, bedding, decor, kitchenware, candles, or soap and bath products",
  },
  pets: {
    vertical: "ecommerce",
    label: "Pets",
    businessName: "pet",
    noun: "pet brand",
    hint: "Sells pet products",
  },
  electronics: {
    vertical: "ecommerce",
    label: "Electronics",
    businessName: "electronics",
    noun: "electronics brand",
    hint: "Sells devices or gadgets",
  },
  "merch-creator": {
    vertical: "ecommerce",
    label: "Merch",
    businessName: "merch",
    noun: "merch store",
    hint: "Sells a creator's or fan merchandise",
  },
  "b2b-software": {
    vertical: "saas",
    label: "B2B software",
    businessName: "B2B software",
    noun: "B2B software company",
    hint: "Software sold to businesses",
  },
  "consumer-apps": {
    vertical: "saas",
    label: "Consumer apps",
    businessName: "consumer app",
    noun: "consumer app",
    hint: "An app for individual consumers",
  },
  games: {
    vertical: "saas",
    label: "Games",
    businessName: "games",
    noun: "game studio",
    hint: "A video game or game studio",
  },
  "developer-tools": {
    vertical: "saas",
    label: "Developer tools",
    businessName: "developer tools",
    noun: "developer tools company",
    hint: "APIs, SDKs or infrastructure for developers",
  },
  restaurants: {
    vertical: "services",
    label: "Restaurants",
    businessName: "restaurant",
    noun: "restaurant",
    hint: "A restaurant, cafe, bar or bakery people visit",
  },
  shops: {
    vertical: "services",
    label: "Shops",
    businessName: "retail",
    noun: "shop",
    hint: "A physical shop that sells other makers' goods (bookshop, paint or hardware store, florist)",
  },
  "salon-spa": {
    vertical: "services",
    label: "Beauty and spa",
    businessName: "beauty and spa",
    noun: "salon or spa",
    hint: "A salon, barber or spa",
  },
  "fitness-wellness": {
    vertical: "services",
    label: "Fitness and wellness",
    businessName: "fitness and wellness",
    noun: "fitness studio",
    hint: "A gym, fitness or yoga studio, or trainer",
  },
  healthcare: {
    vertical: "services",
    label: "Health services",
    businessName: "healthcare",
    noun: "health services business",
    hint: "A clinic, dentist, doctor or therapist",
  },
  "home-services": {
    vertical: "services",
    label: "Home services",
    businessName: "home services",
    noun: "home services business",
    hint: "Cleaning, plumbing, repairs or other trades done at a home",
  },
  "real-estate": {
    vertical: "services",
    label: "Real estate",
    businessName: "real estate",
    noun: "real estate business",
    hint: "A real estate agent, brokerage or property manager",
  },
  "professional-services": {
    vertical: "services",
    label: "Professional services",
    businessName: "professional services",
    noun: "professional services firm",
    hint: "A law firm, accountant, consultancy, agency or design studio",
  },
};

/** A vertical's categories, in the order people are shown them. */
export function getProjectCategories(
  vertical: ProjectVertical,
): ProjectCategory[] {
  return projectCategorySchema.options.filter((category) => {
    return PROJECT_CATEGORIES[category].vertical === vertical;
  });
}

/** A stored category as this code reads it: null when the slug is unknown. With `vertical`, also null when it is tagged with another: art drawn for one workflow set has nothing for the other's trades. */
export function readProjectCategory({
  category,
  vertical,
}: {
  category: string | null | undefined;
  vertical?: ProjectVertical;
}): ProjectCategory | null {
  const known = projectCategorySchema.safeParse(category);
  if (!known.success) {
    return null;
  }
  return !vertical || PROJECT_CATEGORIES[known.data].vertical === vertical
    ? known.data
    : null;
}

/**
 * Words that place a store or a software company, most telling first: the first category with a word in the
 * text wins, so "kitchen software" is software and "protein cereal" is food.
 */
const STORE_AND_SOFTWARE_WORDS: [ProjectCategory, RegExp][] = [
  [
    "merch-creator",
    /\b(merch|merchandise|creators?|fan (club|gear|store)|podcast|youtuber?|streamers?|band tees?)\b/,
  ],
  ["developer-tools", /\b(apis?|sdks?|developer tools?|devops)\b/],
  [
    "b2b-software",
    /\b(saas|software|b2b|crm|erp|analytics|automation|spend management|corporate cards?|payroll|invoicing|accounting|cybersecurity|cloud|hosting|project management|workspace|productivity tools?|no-code|ai (agents?|assistants?|tools?))\b/,
  ],
  [
    "games",
    /\b(gaming|esports|game studios?|(video|mobile|pc|console|indie) games?)\b/,
  ],
  [
    "consumer-apps",
    /\b((mobile )?apps?|streaming|delivery service|food delivery|ride ?sharing|travel booking|dating|language learning|meditation|online (courses?|classes))\b/,
  ],
  [
    "beauty-skincare",
    /\b(beauty|skin ?care|cosmetics?|makeup|hair ?care|fragrances?|perfumes?|nails?|personal care|deodorants?|grooming|shaving|sunscreen)\b/,
  ],
  [
    "food-beverage",
    /\b(food|foods|beverages?|drinks?|coffee|tea|snacks?|olive oil|sauces?|cereal|soda|wine|beer|spirits|chocolate|candy|grocery|pantry|bakery|meal kits?|restaurant|cafe|seltzer|juice|kombucha|spices?)\b/,
  ],
  [
    "health-supplements",
    /\b(supplements?|vitamins?|probiotics?|protein powder|electrolytes?|nutrition|wellness|health|sleep aids?|fitness equipment|greens powder)\b/,
  ],
  [
    "apparel-accessories",
    /\b(apparel|clothing|clothes|fashion|shoes?|sneakers?|footwear|jewelry|jewellery|accessories|wallets?|bags?|handbags?|rainwear|outerwear|activewear|athleisure|swimwear|underwear|socks|eyewear|sunglasses|watches|hats?|denim)\b/,
  ],
  ["pets", /\b(pets?|dogs?|cats?|pupp(y|ies)|kittens?)\b/],
  [
    "home-lifestyle",
    /\b(home|furniture|bedding|sheets|mattress(es)?|cookware|kitchenware|decor|rugs?|candles?|soaps?|bath (bombs?|bars?|salts?)|garden|outdoor|toys?|baby|showerheads?|cleaning|luggage|stationery|plants?)\b/,
  ],
  [
    "electronics",
    /\b(electronics?|headphones?|earbuds|speakers?|laptops?|chargers?|gadgets?|cameras?|keyboards?|smart ?watch(es)?)\b/,
  ],
];

/** Words that name a service business's trade, read only for a project known to be one: a store sells "cleaning" products too. */
const SERVICES_WORDS: [ProjectCategory, RegExp][] = [
  [
    "home-services",
    /\b(clean(ing|ers?)|maids?|housekeeping|janitorial|laundry|landscaping|lawn( care)?|garden(ing|ers?)|plumb(ing|ers?)|electricians?|hvac|roofing|handyman|pest control|movers?|removals?|painters?|home (services|repairs?|improvement))\b/,
  ],
  [
    "salon-spa",
    /\b(salons?|hair(dressers?|stylists?| studio)?|barbers?|barbershops?|nails?|lash(es)?|brows?|wax(ing)?|spas?|massage|facials?|med ?spa|aesthetics?|beauty)\b/,
  ],
  [
    "fitness-wellness",
    /\b(pilates|yoga|barre|gym|fitness|personal train(ing|ers?)|crossfit|wellness|meditation|physio(therapy)?|chiropract(ic|ors?)|dance|studio)\b/,
  ],
  [
    "real-estate",
    /\b(real[- ]estate|realty|realtors?|brokerage|estate agents?|mortgages?|lettings?)\b/,
  ],
  [
    "healthcare",
    /\b(dent(al|ists?)|orthodont(ics|ists?)|clinics?|medical|doctors?|physicians?|pediatric(s|ians?)?|dermatolog(y|ists?)|veterinar(y|ians?)|optometr(y|ists?)|therap(y|ists?)|urgent care|health ?care|counsel(l?ing|l?ors?))\b/,
  ],
  [
    "restaurants",
    /\b(restaurants?|caf[eé]s?|coffee shops?|baker(y|ies)|bistros?|diners?|pizzerias?|catering|food trucks?|(wine|cocktail) bars?|brewer(y|ies))\b/,
  ],
  [
    "professional-services",
    /\b(law (firm|office)s?|lawyers?|attorneys?|legal|account(ing|ants?)|bookkeep(ing|ers?)|tax (prep|preparation|services?)|consult(ing|ants?|ancy)|agency|agencies|architects?|financial advis[eo]rs?|insurance|recruit(ing|ment|ers?)|notary|photograph(y|ers?))\b/,
  ],
  // Last, so "home cleaning" is a cleaner before "home" makes it an estate agent.
  [
    "real-estate",
    /\b(property|properties|homes?|houses?|listings?|apartments?|rentals?)\b/,
  ],
];

/**
 * The category a project's own words name, else null: nothing is guessed from a text no rule matches.
 * `vertical` limits the answer to that vertical's categories; null is a business not yet known to be a store
 * or a software company, which can be either and is never read as a service business.
 */
export function detectProjectCategory({
  vertical,
  texts,
}: {
  vertical: ProjectVertical | null;
  /** What the business says about itself, the most specific description first. */
  texts: (string | null | undefined)[];
}): ProjectCategory | null {
  const rules =
    vertical === "services" ? SERVICES_WORDS : STORE_AND_SOFTWARE_WORDS;
  for (const text of texts) {
    const words = (text ?? "").toLowerCase();
    const match = rules.find(([category, pattern]) => {
      // A store that mentions an app still sells goods, and a software company's "home" page is not homeware.
      const isPossible =
        !vertical || PROJECT_CATEGORIES[category].vertical === vertical;
      return isPossible && pattern.test(words);
    });
    if (match) {
      return match[0];
    }
  }
  return null;
}
